import React from "react";
import { handleImageUploadLogic, useTemplateData } from "@constants/template";
import { templateActions } from "@pages/Applications/C&C/C&CSetup/templateStore";
import { RefreshIcon, StyledIconButton } from "@aureskonnect/react-ui";
import { t } from "i18next";
import swal from "@sweetalert/with-react";

import { deleteFile } from "../fileStore";
import { validUploadedTypes } from "../Website/constants";
import {
    checkDimensions,
    extractDimensions,
    validateFileContent,
} from "../helper";
import { ImageUploadIcon } from "@components/Common/SvgIcons/ImageUploadIcon";
import { ImageUploadFilledIcon } from "@components/Common/SvgIcons/ImageUploadFilledIcon";

interface BannerImageProps {
    dataPath: string[];
    type?: "Desktop" | "Mobile";
    title: string;
    size?: string;
}

const BannerImages = React.memo(
    ({ dataPath, type, title, size }: BannerImageProps) => {
        const { getImageName, getImageUrl, iuud } = useTemplateData();
        const imageUrl = getImageUrl([...dataPath], iuud);
        const imageName = getImageName([...dataPath]);
        const [warningMessage, setWarningMessage] = React.useState("");
        const isUploadingRef = React.useRef(false);
        const path = React.useMemo(() => ["template", ...dataPath], [dataPath]);
        const handleUpload = React.useCallback(
            async (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                const input = e.target;

                if (!file) return;

                if (isUploadingRef.current) return;
                isUploadingRef.current = true;

                try {
                    const isImage = file.type.startsWith("image/");
                    if (!isImage || !validUploadedTypes.includes(file.type)) {
                        return swal({
                            icon: "error",
                            content: <p>{t("File type is not valid")}!</p>,
                            buttons: false,
                            timer: 2000,
                        });
                    }

                    const isRealMedia = await validateFileContent(file);
                    if (!isRealMedia) {
                        return swal({
                            icon: "error",
                            content: (
                                <p>
                                    {t("File content does not match its type")}!
                                </p>
                            ),
                            buttons: false,
                            timer: 2000,
                        });
                    }

                    if (size) {
                        const dimensions = extractDimensions(size);

                        if (dimensions) {
                            const { width, height } = dimensions;

                            const isValid = await checkDimensions(
                                file,
                                width,
                                height
                            );

                            if (!isValid) {
                                setWarningMessage(
                                    "The image does not meet the requirements and may affect the display."
                                );
                            } else {
                                setWarningMessage("");
                            }
                        }
                    }

                    handleImageUploadLogic(
                        path,
                        type,
                        e,
                        templateActions.updateMediaFile
                    );
                } finally {
                    isUploadingRef.current = false;
                    input.value = "";
                }
            },
            [path, type, size]
        );

        const handleReset = React.useCallback(() => {
            if (imageUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(imageUrl);
            }
            const emptyData = {
                Url: "",
                Name: "",
                Type: "",
                Extenxion: "",
            };
            templateActions.updateTemplate(path, emptyData);
            const pathKey = path.join(".");
            deleteFile(pathKey);
            setWarningMessage("");
        }, [path, imageUrl]);

        return (
            <div>
                <div className="image-upload-section">
                    <div>
                        <h6 className="section-text">{t(title)}</h6>
                        <p className="image-specs">
                            {size ? size : "50×50 (img, jpg, jpeg ou png)"}
                        </p>
                    </div>
                    {/* Right side container - flex column with center alignment */}
                    <div className="d-flex flex-column align-items-end">
                        <div className="d-flex align-items-center">
                            {imageUrl !== "" && (
                                <StyledIconButton
                                    title={t("Reset")}
                                    outline={true}
                                    variant="danger"
                                    className="m-0 pme_btn_buttonReinitializeTemplate"
                                    onClick={handleReset}
                                >
                                    <RefreshIcon height={20} width={20} />
                                </StyledIconButton>
                            )}

                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleUpload}
                                className="file-input"
                                id={`${title.toLowerCase()}${dataPath.join(
                                    "-"
                                )}-upload`}
                                aria-label="Upload an image"
                            />
                            <label
                                htmlFor={`${title.toLowerCase()}${dataPath.join(
                                    "-"
                                )}-upload`}
                                style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "0px",
                                }}
                            >
                                {imageUrl !== "" ? (
                                    <ImageUploadFilledIcon
                                        height={40}
                                        width={40}
                                        className="flag__clz"
                                    />
                                ) : (
                                    <ImageUploadIcon
                                        height={40}
                                        width={40}
                                        className="flag__clz"
                                    />
                                )}
                                <span>
                                    {imageUrl !== ""
                                        ? imageName.slice(-15)
                                        : ""}
                                </span>
                            </label>
                        </div>
                        {warningMessage !== "" && (
                            <div
                                style={{
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                    textAlign: "center",
                                }}
                            >
                                {t(warningMessage)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export default BannerImages;
